/* eslint-disable max-lines */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ExpandIcon from '@assets/images/select-expand-icon.svg?react'
import CheckIcon from '@assets/images/check-icon.svg?react'

import { Loader } from '../Loader'
import { DropDown, DropDownActions } from '../DropDown'

import {
  Container,
  Label,
  LabelOptional,
  TextInputContainer,
  ContainerSelect,
  Item,
  ItemLabel,
  ItemHint,
  EmptyItemLabel,
  TextInput,
  LoaderContainer,
  ExpandContainer,
  Placeholder,
  ItemContainer,
  IconContainer,
  FakeInput,
} from './Select.styled'

export interface Option {
  id: string
  label: string
  hint?: string
  Component?: () => JSX.Element
  disabled?: boolean
  data: any
}

interface Props {
  label?: string
  emptyLabel?: string
  options: Option[]
  selected?: Option
  onClick: (option?: Option) => void
  required?: boolean
  disabled?: boolean
  search?: string
  onSearch?: (value: string) => void
  loading?: boolean
  style?: React.CSSProperties
  classNames?: {
    container?: string
    textInputContainer?: string
    expandContainer?: string
  }
  autoFocus?: boolean
  placeholder?: string
  refInput?: (ref: HTMLInputElement | null) => void
  preventDropDownOnAutofocus?: boolean
  disabledVerticalCorrection?: boolean
  autoHide?: boolean
  dropdownAutoWidth?: boolean
  disableAutoScroll?: boolean
  hideOptional?: boolean
  WrappedComponent?: (props: DropDownActions) => JSX.Element
  FooterComponent?: (props: DropDownActions) => JSX.Element
  onHide?: () => void
}

export const Select = (props: Props): JSX.Element => {
  const {
    label,
    emptyLabel,
    selected,
    options,
    style,
    onClick,
    required,
    disabled,
    search,
    onSearch,
    loading,
    classNames,
    autoFocus,
    placeholder,
    refInput,
    preventDropDownOnAutofocus,
    disabledVerticalCorrection,
    WrappedComponent,
    FooterComponent,
    autoHide = true,
    dropdownAutoWidth = false,
    disableAutoScroll = false,
    hideOptional = false,
    onHide,
  } = props

  const { t } = useTranslation()

  const refElement = useRef<HTMLDivElement | null>(null)
  const refSelectElement = useRef<HTMLDivElement | null>(null)

  const selectedIndex = options.findIndex((o) => o.id == selected?.id)
  const enabledIndex = options.findIndex((o) => !o.disabled)

  const [highlighted, setHighlighted] = useState<number>(selectedIndex == -1 ? enabledIndex : selectedIndex)

  const [preventDropDownAutofocus, setPreventDropDownAutofocus] = useState<boolean>(preventDropDownOnAutofocus ?? false)

  useEffect(() => {
    setPreventDropDownAutofocus(false)
  }, [])

  useEffect(() => {
    const currentIndex = selectedIndex == -1 ? enabledIndex : selectedIndex
    setHighlighted(currentIndex)

    if (disableAutoScroll) return

    const option = options[currentIndex]
    if (option) {
      document.getElementById(`select-item-${option.id}`)?.scrollIntoView(true)
      refSelectElement.current?.scrollBy({ top: -4 })
    }
  }, [options, selectedIndex, enabledIndex, disableAutoScroll])

  const refDisableMouseEnter = useRef<boolean>(false)
  const refDisableOnBlur = useRef<boolean>(false)

  const getNextAvailableOption = useCallback(
    (index: number, increment: -1 | 1): { option: Option | undefined; index: number } => {
      const option = options[index]
      if (!option) return { option: undefined, index }

      if (option.disabled) {
        return getNextAvailableOption(index + increment, increment)
      }

      return { option, index }
    },
    [options],
  )

  const onKeyDown = useCallback(
    (event: KeyboardEvent, actions: DropDownActions) => {
      if (event.key == 'ArrowDown') {
        event.preventDefault()
        event.stopPropagation()

        if (highlighted < options.length - 1) {
          refDisableMouseEnter.current = true

          const index = highlighted + 1

          const { option, index: nextIndex } = getNextAvailableOption(index, 1)
          if (!option) return

          const element = document.getElementById(`select-item-${option.id}`)

          setHighlighted(nextIndex)

          if (element && refSelectElement.current) {
            const margin = refSelectElement.current?.scrollTop + refSelectElement.current?.clientHeight

            if (element?.offsetTop + 42 > margin) {
              element.scrollIntoView({ block: 'end' })
              refSelectElement.current?.scrollBy({ top: 4 })
            }
          }
        }
      } else if (event.key == 'ArrowUp') {
        event.preventDefault()
        event.stopPropagation()

        refDisableMouseEnter.current = true

        if (highlighted > 0) {
          const index = highlighted - 1

          const { option, index: nextIndex } = getNextAvailableOption(index, -1)
          if (!option) return

          const element = document.getElementById(`select-item-${option.id}`)

          setHighlighted(nextIndex)

          if (element && refSelectElement.current) {
            const margin = refSelectElement.current?.scrollTop

            if (element?.offsetTop < margin) {
              element.scrollIntoView({ block: 'start' })
              refSelectElement.current?.scrollBy({ top: -4 })
            }
          }
        }
      } else if (event.key == 'Enter') {
        const option = options[highlighted]
        onClick(option)

        event.preventDefault()
        event.stopPropagation()

        actions.hide()
      }
    },
    [getNextAvailableOption, highlighted, onClick, options],
  )

  return (
    <DropDown
      disabled
      onHide={() => {
        refDisableOnBlur.current = false

        onHide?.()
      }}
      onKeyDown={onKeyDown}
      disabledVerticalCorrection={disabledVerticalCorrection}
      WrappedComponent={({ show, hide, ...p }) =>
        WrappedComponent ? (
          <WrappedComponent {...{ show, hide, ...p }} />
        ) : (
          <Container ref={refElement} className={classNames?.container}>
            {label && (
              <Label>
                {label}
                {!required && !hideOptional && (
                  <LabelOptional>{` (${t('components_Select_label_optional')})`}</LabelOptional>
                )}
              </Label>
            )}
            <TextInputContainer
              className={'text-input ' + classNames?.textInputContainer}
              onClick={disabled ? undefined : onSearch ? () => show() : () => (p.display ? hide() : show())}
              style={{ cursor: disabled ? 'default' : 'pointer', ...style }}
            >
              {selected?.Component && <selected.Component />}
              {onSearch ? (
                <TextInput
                  ref={(ref) => {
                    refInput?.(ref)
                  }}
                  required={required}
                  disabled={disabled}
                  placeholder={placeholder}
                  autoFocus={autoFocus}
                  value={search}
                  onChange={(e) => {
                    onSearch?.(e.target.value)
                  }}
                  onKeyDown={(e) => {
                    if (e.key != 'Escape' && e.key != 'Enter') {
                      show()
                    }
                  }}
                  onFocus={() => {
                    if (!preventDropDownAutofocus) {
                      show()
                    }
                  }}
                  onBlur={() => {
                    if (!refDisableOnBlur.current) {
                      onClick(selected)

                      autoHide && hide()
                    }
                  }}
                />
              ) : (
                <>
                  <Placeholder className="placeholder" style={{ flex: 1, cursor: disabled ? 'default' : 'pointer' }}>
                    {!selected?.Component && placeholder}
                  </Placeholder>
                  <FakeInput
                    type="text"
                    onChange={() => {}}
                    required={required}
                    value={selected?.id || ''}
                    style={{ opacity: 0, cursor: disabled ? 'default' : 'pointer' }}
                  />
                </>
              )}

              {!loading && (
                <ExpandContainer disabled={disabled} className={classNames?.expandContainer}>
                  <ExpandIcon />
                </ExpandContainer>
              )}

              {loading && (
                <LoaderContainer>
                  <Loader size={20} />
                </LoaderContainer>
              )}
            </TextInputContainer>
          </Container>
        )
      }
      WrappedChildren={({ hide, ...p }) => (
        <ContainerSelect
          id="select-container"
          ref={refSelectElement}
          width={refElement.current?.clientWidth}
          dropdownAutoWidth={dropdownAutoWidth}
          onMouseMove={() => {
            refDisableOnBlur.current = true
          }}
          onMouseLeave={() => {
            refDisableOnBlur.current = false
          }}
        >
          {options.map((option, index) => (
            <Item
              id={`select-item-${option.id}`}
              key={option.id}
              highlighted={highlighted == index}
              selected={option.id == selected?.id}
              disabled={option.disabled}
              onClick={(e) => {
                if (option.disabled) return

                e.preventDefault()

                onClick(option)

                autoHide && hide()
              }}
              onMouseEnter={() => {
                if (option.disabled) return

                if (refDisableMouseEnter.current == false) {
                  setHighlighted(index)
                }

                refDisableMouseEnter.current = false
              }}
              onMouseMove={() => {
                if (option.disabled) return

                setHighlighted(index)
              }}
            >
              {option.Component ? (
                <option.Component />
              ) : (
                <ItemContainer>
                  <ItemLabel selected={option.id == selected?.id}>{option.label}</ItemLabel>

                  {option.id == selected?.id && (
                    <IconContainer>
                      <CheckIcon />
                    </IconContainer>
                  )}
                </ItemContainer>
              )}
              {option.hint && !option.Component && <ItemHint>{option.hint}</ItemHint>}
            </Item>
          ))}
          {options.length == 0 && (
            <Item disabled key="no_data">
              <EmptyItemLabel>{emptyLabel || t('components_Select_empty')}</EmptyItemLabel>
            </Item>
          )}
          {FooterComponent && (
            <div
              onMouseDown={(e) => {
                e.preventDefault()
              }}
            >
              <FooterComponent {...{ hide, ...p }} />
            </div>
          )}
        </ContainerSelect>
      )}
    />
  )
}
